/* Base Layout
Author: Maxwell Levene @maxlevene920- Ellmarr
Date: 04/30/2025
Description: Transactions page component for managing carbon credit transactions.
Displays transaction history with admin editing capabilities for payment status.
*/

import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronLeft, ChevronRight, CreditCard, User, Calendar, Download } from "lucide-react";
import { transactionsService, Transaction } from "@/api/transactionsService";
import { toast } from "sonner";

const PAGE_SIZE = 25;

const TransactionsPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  // Fetch transactions
  const { data, isLoading, error } = useQuery({
    queryKey: ["transactions", page],
    queryFn: async () => {
      return await transactionsService.getTransactions(page, PAGE_SIZE);
    },
    refetchOnWindowFocus: false,
  });

  // Update transaction mutation
  const updateTransactionMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: { status?: "paid" | "pending" } }) => {
      return await transactionsService.updateTransaction(id, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["transactions"] });
      toast.success("Transaction updated successfully");
    },
    onError: (error) => {
      console.error("Error updating transaction:", error);
      toast.error("Failed to update transaction");
    },
  });

  const transactions = data?.transactions || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handlePaymentStatusChange = (transactionId: string, paid: boolean) => {
    updateTransactionMutation.mutate({
      id: transactionId,
      updates: { status: paid ? "paid" : "pending" }
    });
  };

  const exportTransactionsCSV = async () => {
    try {
      // Fetch all transactions for export (not just current page)
      const allTransactionsData = await transactionsService.getTransactions(1, 10000);
      const allTransactions = allTransactionsData.transactions;
      
      if (allTransactions.length === 0) {
        toast.error("No transactions to export");
        return;
      }

      const exportData = allTransactions.map(transaction => ({
        Date: format(new Date(transaction.created_at), "MMM dd, yyyy"),
        Time: format(new Date(transaction.created_at), "HH:mm"),
        User: transaction.user?.username || transaction.user_id,
        Credits: transaction.credits,
        "Value (£)": transaction.value.toFixed(2),
        Status: transaction.status,
        "Transaction ID": transaction.id,
      }));

      const csvContent = [
        Object.keys(exportData[0]).join(','),
        ...exportData.map(row => 
          Object.values(row).map(value => 
            typeof value === 'string' && value.includes(',') ? `"${value}"` : value
          ).join(',')
        )
      ].join('\n');
      
      const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const csvUrl = URL.createObjectURL(csvBlob);
      const csvLink = document.createElement('a');
      csvLink.href = csvUrl;
      csvLink.setAttribute('download', `all_carbon_credit_transactions_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(csvLink);
      csvLink.click();
      document.body.removeChild(csvLink);
      URL.revokeObjectURL(csvUrl);

      toast.success(`Successfully exported ${allTransactions.length} transactions`);
    } catch (error) {
      console.error("Error exporting transactions:", error);
      toast.error("Failed to export transactions");
    }
  };

  const renderPaginationButtons = () => {
    const pages = [];
    const maxPagesToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, page + 2);
    
    if (page <= 3) {
      end = Math.min(totalPages, maxPagesToShow);
    } else if (page >= totalPages - 2) {
      start = Math.max(1, totalPages - maxPagesToShow + 1);
    }
    
    if (start > 1) {
      pages.push(
        <button
          key={1}
          className={`px-2 py-1 rounded ${page === 1 ? 'bg-muted font-bold' : 'hover:bg-muted'}`}
          onClick={() => setPage(1)}
        >
          1
        </button>
      );
      if (start > 2) pages.push(<span key="start-ellipsis" className="px-1">...</span>);
    }
    
    for (let i = start; i <= end; i++) {
      pages.push(
        <button
          key={i}
          className={`px-2 py-1 rounded ${page === i ? 'bg-muted font-bold' : 'hover:bg-muted'}`}
          onClick={() => setPage(i)}
          disabled={page === i}
        >
          {i}
        </button>
      );
    }
    
    if (end < totalPages) {
      if (end < totalPages - 1) pages.push(<span key="end-ellipsis" className="px-1">...</span>);
      pages.push(
        <button
          key={totalPages}
          className={`px-2 py-1 rounded ${page === totalPages ? 'bg-muted font-bold' : 'hover:bg-muted'}`}
          onClick={() => setPage(totalPages)}
        >
          {totalPages}
        </button>
      );
    }
    
    return pages;
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <p className="text-destructive">Error loading transactions: {error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Transactions</h1>
          <p className="text-muted-foreground">
            Manage carbon credit transactions and payment status
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {total} total transactions
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={exportTransactionsCSV}
            disabled={total === 0}
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="h-5 w-5" />
            Carbon Credit Transactions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Credits</TableHead>
                <TableHead>Amount (£)</TableHead>
                <TableHead>Payment Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                // Loading skeletons
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                    <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                    <TableCell><Skeleton className="h-8 w-16" /></TableCell>
                  </TableRow>
                ))
              ) : transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <div className="flex flex-col items-center justify-center">
                      <CreditCard className="h-12 w-12 text-muted-foreground mb-2" />
                      <p className="text-muted-foreground font-medium">No transactions found</p>
                      <p className="text-xs text-muted-foreground">
                        Carbon credit transactions will appear here when available.
                      </p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((transaction) => (
                  <TableRow key={transaction.id} className="hover:bg-muted/50">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <div className="font-medium">
                            {format(new Date(transaction.created_at), "dd MMM yyyy")}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {format(new Date(transaction.created_at), "HH:mm")}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <div>
                          {transaction.user?.username ? (
                            <Link 
                              to={`/users/${transaction.user_id}`}
                              className="font-medium text-primary hover:underline"
                            >
                              {transaction.user.username}
                            </Link>
                          ) : (
                            <span className="font-medium text-muted-foreground">
                              {transaction.user_id}
                            </span>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <span className="font-medium">{transaction.credits}</span>
                        <span className="text-xs text-muted-foreground">credits</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">
                        £{transaction.value.toFixed(2)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={transaction.status === "paid" ? "default" : "secondary"}>
                        {transaction.status === "paid" ? "Paid" : "Unpaid"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={transaction.status === "paid"}
                          onCheckedChange={(checked) => 
                            handlePaymentStatusChange(transaction.id, checked)
                          }
                          disabled={updateTransactionMutation.isPending}
                        />
                        <span className="text-xs text-muted-foreground">
                          {transaction.status === "paid" ? "Paid" : "Mark Paid"}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center mt-4 gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                aria-label="Previous"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              {renderPaginationButtons()}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                aria-label="Next"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TransactionsPage; 